import React from "react";
import cn from "./helpers/cn";
import { RadioGroupContext, } from "./RadioGroup";

export interface RadioProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  color?: "primary" | "secondary" | "default";
}

const Radio = React.forwardRef<HTMLInputElement, RadioProps>((props, ref) => {
  const group = React.useContext(RadioGroupContext);

  const {
    label,
    color = "primary",
    className,
    disabled,
    value,
    name: nameProp,
    checked: checkedProp,
    onChange: onChangeProp,
    ...other
  } = props;

  // Lógica de estado vinda do RadioGroup ou local
  const isChecked = group ? String(group.value) === String(value) : checkedProp;
  const name = group?.name || nameProp;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Dispara o onChange individual se existir
    if (onChangeProp) {
      onChangeProp(event);
    }
    // Dispara o onChange do Grupo passando (evento, valor)
    if (group?.onChange) {
      group.onChange(event, value);
    }
  };

  return (
    <label className={cn(
      "inline-flex items-center cursor-pointer group",
      disabled && "cursor-default opacity-50",
      className
    )}>
      <div className="relative flex items-center justify-center p-2 rounded-full hover:bg-black/5 transition-colors">
        <input
          type="radio"
          ref={ref}
          name={name}
          value={value}
          checked={isChecked}
          onChange={handleChange}
          disabled={disabled}
          className="peer absolute opacity-0 w-full h-full cursor-pointer z-10"
          {...other}
        />

        {/* Círculo Externo */}
        <div className={cn(
          "w-5 h-5 border-2 border-slate-400 rounded-full transition-all duration-200",
          "peer-checked:border-current", // Usa a cor do texto definida abaixo
          "peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-primary",
          color === "primary" && "text-primary",
          color === "secondary" && "text-secondary",
          color === "default" && "text-slate-600"
        )} />

        {/* Ponto Interno (Escala) */}
        <div className={cn(
          "absolute w-2.5 h-2.5 rounded-full transform scale-0 transition-transform duration-200 peer-checked:scale-100",
          color === "primary" && "bg-primary",
          color === "secondary" && "bg-secondary",
          color === "default" && "bg-slate-600"
        )} />
      </div>

      {label && (
        <span className="ml-1 text-sm text-slate-700 font-medium select-none">
          {label}
        </span>
      )}
    </label>
  );
});

Radio.displayName = "Radio";

export default Radio;